import { downloadBlob } from './download';

// Spreadsheet formula injection (CWE-1236): a leading =, +, -, @ or tab makes
// Excel/Sheets evaluate the cell. Plain numbers (e.g. -100) are exempt — they
// can't be formulas and prefixing them would corrupt numeric columns.
const needsFormulaGuard = (text: string): boolean => /^[=+\-@\t]/.test(text) && !Number.isFinite(Number(text));

const escapeCell = (value: unknown): string => {
  const raw = value === null || value === undefined ? '' : String(value);
  const text = needsFormulaGuard(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/**
 * Parse RFC-4180 CSV text: quoted fields, `""` escapes, commas/newlines inside
 * quotes, CRLF or LF row breaks. The first row is treated as the header.
 */
export const parseCsv = (text: string): ParsedCsv => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let index = 0;

  const endField = () => {
    row.push(field);
    field = '';
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (index < text.length) {
    const char = text[index];
    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 2;
          continue;
        }
        inQuotes = false;
        index += 1;
        continue;
      }
      field += char;
      index += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      index += 1;
      continue;
    }
    if (char === ',') {
      endField();
      index += 1;
      continue;
    }
    if (char === '\r' && text[index + 1] === '\n') {
      endRow();
      index += 2;
      continue;
    }
    if (char === '\n' || char === '\r') {
      endRow();
      index += 1;
      continue;
    }
    field += char;
    index += 1;
  }
  // Flush the trailing field/row unless the input ended exactly on a row break.
  if (field !== '' || row.length > 0) endRow();

  const [headers = [], ...dataRows] = rows;
  return { headers, rows: dataRows };
};

/** Serialize rows to CSV with an explicit column order (header row = column keys) */
export const toCsv = (rows: Array<Record<string, unknown>>, columns: string[]): string =>
  [columns.join(','), ...rows.map((row) => columns.map((column) => escapeCell(row[column])).join(','))].join('\n');

/** Serialize already-positional rows (e.g. a parsed CSV filtered client-side) back to CSV. */
export const rowsToCsv = (headers: string[], rows: string[][]): string => [headers.map(escapeCell).join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n');

export const downloadCsv = (csv: string, filename: string): void => downloadBlob(csv, filename, 'text/csv;charset=utf-8');
